import {
    deviceId,
    refreshUserData,
    isSubscriptionAvailable,
    getHomePage,
    isParentalSetupComplete,
    isPostTvPlatform,
    isPostTvLinux,
    isPostTvAndroid,
    PLATFORMS,
    STORAGE_KEYS
} from "../lib/utils";
import { postTvWebSocket } from "../lib/websocket";

import configs from "../lib/configs";

// import * as Sentry from "@sentry/browser";
import posthog from "posthog-js";

import { Parameters } from "@firebolt-js/sdk";
import { Profile, Router, Storage } from "@lightningjs/sdk";

import { getAvailableFilters, getSubscriptionData, getSubscriptionDataAndFilters } from "../api/queries";

import { Settings as SettingsPage } from "../pages/settings/Settings";
import { ErrorPage } from "../pages/ErrorPage";
import { Boot } from "../pages/Boot";
import { InfoPage } from "../pages/settings/Info";
import routes from "./routes";
import { NotFoundPage } from "../pages/NotFoundPage";

interface NavigateToData {
    entityId: string;
    programType: string;
}

interface DiscoveryParams {
    navigateTo?: {
        data: NavigateToData;
    };
}

interface InitParams {
    discovery?: DiscoveryParams;
}

export default {
    boot: () => {
        return new Promise<void>((resolve) => {
            (async () => {
                const query = Router.getQueryStringParams();
                const queryPlatform = query ? query.platform : undefined;

                const userAgent = navigator.userAgent.toLowerCase();

                const isSamsung = typeof window.tizen !== "undefined";
                const isLG = /(webos|web0s)/i.test(userAgent);

                const tvPlatform = isSamsung
                    ? PLATFORMS.samsung
                    : isLG
                      ? PLATFORMS.lg
                      : userAgent.indexOf("whaletv") > 0
                        ? PLATFORMS.whaletv
                        : userAgent.indexOf("philips") > 0
                          ? PLATFORMS.philips
                          : userAgent.indexOf("nettv") > 0
                            ? PLATFORMS.nettv
                            : typeof window.Hisense_GetDeviceID === "function"
                              ? PLATFORMS.hisense
                              : undefined;

                let platform = queryPlatform || tvPlatform || (await Profile.platform());
                platform = platform.toLowerCase() === "wpe" ? PLATFORMS.comcast : platform;
                Storage.set(STORAGE_KEYS.platform, platform);

                if (isPostTvPlatform()) {
                    if (
                        !Storage.get(STORAGE_KEYS.externalToken) ||
                        (query && query.token && Storage.get(STORAGE_KEYS.token) === query.token)
                    ) {
                        // temporary clean up, token should be internal, posttv token is external now
                        Storage.remove(STORAGE_KEYS.token);
                    }
                    Storage.set(STORAGE_KEYS.externalToken, query ? query.token : "");
                    Storage.set(STORAGE_KEYS.posttvOS, query && query.android ? "android" : "linux");

                    // Initialize WebSocket for posttv Linux (only if useParentalPin is enabled)
                    // const useParentalPin = configs.useParentalPin === true;
                    // if (isPostTvLinux() && useParentalPin) {
                    //     try {
                    //         postTvWebSocket.create({
                    //             onOpen: () => {
                    //                 console.log("[PostTV Linux] WebSocket connected, calling getSettings('pics.pin')");
                    //                 try {
                    //                     postTvWebSocket.getSettings("pics.pin", (response) => {
                    //                         console.log("[PostTV Linux] getSettings('pics.pin') response:", response);
                    //                     });
                    //                 } catch (e) {
                    //                     console.error("[PostTV Linux] Error calling getSettings:", e);
                    //                 }
                    //             },
                    //             onError: (error) => {
                    //                 console.error("[PostTV Linux] WebSocket error:", error);
                    //             },
                    //             onClose: () => {
                    //                 console.log("[PostTV Linux] WebSocket closed");
                    //             }
                    //         });
                    //     } catch (e) {
                    //         console.error("[PostTV Linux] Error initializing WebSocket:", e);
                    //     }
                    // }
                    //
                    // // Initialize posttv Android testPinCode on app start
                    // if (isPostTvAndroid()) {
                    //     try {
                    //         const android = window.Android;
                    //         if (
                    //             typeof android !== "undefined" &&
                    //             android &&
                    //             typeof android.testPinCode === "function"
                    //         ) {
                    //             console.log("[PostTV Android] Calling Android.testPinCode(1111)");
                    //             console.log("[PostTV Android] result ", android.testPinCode("1111"));
                    //             console.log("[PostTV Android] result ", android.testPinCode("1110"));
                    //         } else {
                    //             console.warn("[PostTV Android] Android.testPinCode not available");
                    //         }
                    //     } catch (e) {
                    //         console.error("[PostTV Android] Error calling Android.testPinCode:", e);
                    //     }
                    // }
                }

                // if (process.env.SENTRY_ENABLED !== "false") {
                //     Sentry.init({
                //         dsn: "https://5ff736e92e846291607d9e41001f819b@sentry.alteox.app/2"
                //     });
                //     Sentry.setTag("app_id", process.env.APP_ID || "unknown");
                // }

                const generatedDeviceId = await deviceId();

                // metrological is a local build
                if (platform !== "metrological" && configs.posthog) {
                    posthog.init(configs.posthog, {
                        api_host: "https://app.posthog.com",
                        autocapture: false
                    });
                }

                // refreshUserData must complete first as it sets the auth token needed by other API calls
                await refreshUserData(true);

                // Track users in PostHog after user data is loaded
                if (platform !== "metrological" && configs.posthog) {
                    const user = Storage.get(STORAGE_KEYS.user);
                    const identifyProperties: any = {
                        platform: Storage.get(STORAGE_KEYS.platform)
                    };

                    const captureProperties: any = {};

                    if (user?.whiteListed) {
                        identifyProperties.isVodafoneUser = true;
                        identifyProperties.whiteListed = true;
                        captureProperties.isVodafoneUser = true;
                        captureProperties.whiteListed = true;
                    }

                    try {
                        posthog.identify(generatedDeviceId, identifyProperties);
                        posthog.capture("AppStart", captureProperties);
                    } catch (e) {
                        console.error("PostHog error:", e);
                    }
                }

                // Batch GraphQL queries: if both subscription and filters are needed, fetch them in a single request
                const needsSubscription = isSubscriptionAvailable();
                const needsFilters = configs.layout?.filters?.enabled;

                if (needsSubscription && needsFilters) {
                    // Batch both queries into a single GraphQL request
                    try {
                        const { subscriptionsList, vodMeta } = await getSubscriptionDataAndFilters();
                        Storage.set("subscriptionData", subscriptionsList);
                        Storage.set("filters", vodMeta);
                    } catch (e) {
                        console.log("getSubscriptionDataAndFilters err", e);
                        // Fallback to individual requests if batch fails
                        try {
                            const subData = await getSubscriptionData();
                            Storage.set("subscriptionData", subData);
                        } catch (subErr) {
                            console.log("subscriptionData err", subErr);
                        }
                        try {
                            const filters = await getAvailableFilters();
                            Storage.set("filters", filters);
                        } catch (filterErr) {
                            console.log("getAvailableFilters err", filterErr);
                        }
                    }
                } else {
                    // Run individual queries in parallel if only one is needed
                    const parallelPromises: Promise<any>[] = [];

                    if (needsSubscription) {
                        parallelPromises.push(
                            getSubscriptionData()
                                .then((subData) => {
                                    Storage.set("subscriptionData", subData);
                                    return subData;
                                })
                                .catch((e) => {
                                    console.log("subscriptionData err", e);
                                    return null;
                                })
                        );
                    }

                    if (needsFilters) {
                        parallelPromises.push(
                            getAvailableFilters()
                                .then((filters) => {
                                    Storage.set("filters", filters);
                                    return filters;
                                })
                                .catch((e) => {
                                    console.log("getAvailableFilters err", e);
                                    return null;
                                })
                        );
                    }

                    // Wait for both to complete in parallel
                    if (parallelPromises.length > 0) {
                        await Promise.allSettled(parallelPromises);
                    }
                }

                Router.resume();

                // Check if parental setup is needed
                if (!isParentalSetupComplete()) {
                    console.log("[boot] Parental setup not complete, redirecting...");
                    setTimeout(() => {
                        Router.navigate("parental-setup", { replace: true });
                    }, 100);
                }

                // timeout for build only
                // for some reason next requests are being sent without token
                setTimeout(() => {
                    resolve();
                }, 1);
            })().catch((error) => {
                console.error("Boot initialization error:", error);
                // Still resolve to allow app to continue even if initialization fails
                Router.resume();

                // Check if parental setup is needed
                if (!isParentalSetupComplete()) {
                    setTimeout(() => {
                        Router.navigate("parental-setup", { replace: true });
                    }, 100);
                }

                // timeout for build only
                // for some reason next requests are being sent without token
                setTimeout(() => {
                    resolve();
                }, 1);
            });
        });
    },
    root: () => {
        return new Promise<string>((resolve) => {
            // Check if parental setup is needed before showing any content
            const setupComplete = isParentalSetupComplete();
            console.log("[root] isParentalSetupComplete:", setupComplete, "parentControl:", configs.settings?.parentControl);
            if (!setupComplete) {
                console.log("[root] Redirecting to parental-setup");
                resolve("parental-setup");
                return;
            }

            // On Comcast/RDK first cold-start the Firebolt WebSocket transport is often
            // not yet connected when root() runs.  The SDK queues the request and waits
            // indefinitely instead of rejecting, so the .catch below never fires and the
            // app appears frozen.  A 5-second timeout guarantees we always navigate home.
            let settled = false;
            const fallback = (reason?: any) => {
                if (settled) return;
                settled = true;
                if (reason) console.log("[root] Parameters.initialization fallback:", reason);
                resolve(getHomePage());
            };

            const timeoutId = setTimeout(() => fallback("timeout"), 5000);

            Parameters.initialization()
                .then((init: InitParams) => {
                    clearTimeout(timeoutId);
                    if (settled) return;
                    settled = true;

                    if (init.discovery?.navigateTo?.data) {
                        const { entityId, programType } = init.discovery?.navigateTo?.data;

                        if (entityId !== "abc") {
                            resolve(`${programType === "series" ? "series" : "movie"}/${entityId}`);
                            return;
                        }
                    }

                    resolve(getHomePage());
                })
                .catch((error) => {
                    clearTimeout(timeoutId);
                    fallback(error);
                });
        });
    },
    routes: [
        {
            path: "$",
            component: Boot
        },
        ...routes,
        {
            path: "settings",
            component: SettingsPage,
            widgets: [
                <Lowercase<string>>"Menu",
                <Lowercase<string>>"Subscription",
                <Lowercase<string>>"Auth",
                <Lowercase<string>>"IAPSubscription",
                <Lowercase<string>>"PostTvSubscription",
                <Lowercase<string>>"Exit"
            ],
            options: {
                clearHistory: true
            }
        },
        {
            path: "info",
            component: InfoPage,
            widgets: [
                <Lowercase<string>>"Menu",
                <Lowercase<string>>"Subscription",
                <Lowercase<string>>"Auth",
                <Lowercase<string>>"IAPSubscription",
                <Lowercase<string>>"PostTvSubscription",
                <Lowercase<string>>"Exit"
            ]
        },
        {
            path: "!",
            component: ErrorPage,
            widgets: [<Lowercase<string>>"Menu", <Lowercase<string>>"Exit"],
            options: {}
        },
        {
            path: "*",
            component: NotFoundPage,
            widgets: [<Lowercase<string>>"Menu", <Lowercase<string>>"Exit"],
            options: {}
        }
    ]
};
